/**
 * The shape of the product, in one file.
 *
 * Every nav entry, the roadmap panels, and the sidebar are all generated from
 * this. Adding a pillar is one entry here rather than a route, a nav item and a
 * stub page that can drift apart.
 *
 * `built: false` is deliberate and load-bearing. It means "the API does not
 * exist yet" -- not "the UI is unfinished". Only `customers` is built, because
 * `/contacts` is the only data endpoint that exists. Marking a screen as
 * available when its endpoint is missing would produce a demo that lies.
 */
import type { LucideIcon } from "lucide-react";
import { WORKFORCE_REPORT_PERSONAS } from "../lib/access";
import {
  BadgeDollarSign,
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  CalendarDays,
  ClipboardList,
  Clock,
  Cog,
  CreditCard,
  FileText,
  Kanban,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  Network,
  Package,
  Percent,
  Receipt,
  Repeat,
  Share2,
  Sparkles,
  Trash2,
  UserSquare2,
  Users,
  Wallet,
} from "lucide-react";

import type { Persona } from "../lib/persona";

export type PillarId =
  | "customers"
  | "scheduling"
  | "people"
  | "billing"
  | "automation"
  | "reports";

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** End of the build phase that ships this. Drives the "planned" copy. */
  phase: number;
  built: boolean;
  /** Exact match only. Set when another item's path is a prefix of this one. */
  end?: boolean;
  /** One line on what the screen is for. Shown in the roadmap panel. */
  purpose?: string;
  /**
   * Which workspaces show this item. Omitted means every persona. This filters
   * navigation only -- the API independently enforces who may read the data.
   */
  roles?: Persona[];
}

export interface Pillar {
  id: PillarId;
  label: string;
  /**
   * What this group is called for the employee workspace. The same five
   * screens are management screens to an owner and an HR section to the
   * person whose own records they are, so the employee sees a group whose
   * name matches what is in it.
   */
  employeeLabel?: string;
  /** Shown in the dashboard strip so the three parts are legible at a glance. */
  blurb: string;
  /** Module code from the API's plan.modules list, when there is one. */
  moduleCode?: string;
  /** Which workspaces see the pillar. Omitted means every persona. */
  roles?: Persona[];
  items: NavItem[];
}

export const PILLARS: Pillar[] = [
  {
    id: "customers",
    label: "Customers",
    blurb: "Everyone who buys from you, and every deal they are in.",
    moduleCode: "customers",
    roles: ["owner", "manager"],
    items: [
      {
        label: "Dashboard",
        to: "/dashboard",
        icon: LayoutDashboard,
        phase: 1,
        built: true,
        purpose: "Pipeline value, tasks due and revenue at a glance.",
      },
      {
        label: "Contacts",
        to: "/customers/contacts",
        icon: Users,
        phase: 1,
        built: true,
        purpose: "The master record. Search, tag and follow up.",
      },
      {
        label: "Leads",
        to: "/customers/leads",
        icon: UserSquare2,
        phase: 1,
        built: true,
        purpose: "Every enquiry, open ones first, with age so stale ones surface.",
      },
      {
        label: "Pipeline",
        to: "/customers/pipeline",
        icon: Kanban,
        phase: 2,
        built: false,
        purpose: "Deals on a configurable board with weighted value.",
      },
      {
        // The per-record timeline IS live -- it is on the contact screen. What
        // is not built is the cross-contact view this route would be: every
        // activity across the account, filterable by kind and assignee.
        label: "Activity log",
        to: "/customers/activities",
        icon: ListChecks,
        phase: 2,
        built: false,
        purpose: "Every logged call, note and task across the account.",
      },
      {
        label: "Bin",
        to: "/customers/trash",
        icon: Trash2,
        phase: 1,
        built: true,
        purpose: "Deleted records, kept 30 days and restorable.",
      },
    ],
  },
  {
    id: "scheduling",
    label: "Scheduling",
    blurb: "The catalogue you sell from, the calendar, and the bookings that fill it.",
    roles: ["owner", "manager"],
    items: [
      {
        label: "Catalog",
        to: "/catalog",
        icon: Package,
        phase: 2,
        built: true,
        purpose: "What you sell, what it costs you, and how long it takes.",
      },
      {
        label: "Customers",
        to: "/scheduling/customers",
        icon: Users,
        phase: 1,
        built: true,
        purpose: "Everyone who became a customer, with the contact behind them.",
      },
      {
        label: "Calendar",
        to: "/scheduling/calendar",
        icon: CalendarDays,
        phase: 2,
        built: true,
        purpose: "Bookings on a week view, per person and per resource.",
      },
      {
        label: "Bookings",
        to: "/scheduling/bookings",
        icon: BookOpen,
        phase: 2,
        built: true,
        purpose: "Double-booking is rejected by the database, not by luck.",
      },
    ],
  },
  {
    id: "people",
    label: "People",
    employeeLabel: "HR",
    blurb: "Your team: who they are, when they work, and what they are paid.",
    moduleCode: "people",
    items: [
      {
        label: "Employees",
        to: "/people/employees",
        icon: Briefcase,
        phase: 5,
        built: true,
        roles: ["owner", "manager"],
        purpose: "The employment register, profiles and organization.",
      },
      {
        label: "Attendance",
        to: "/people/attendance",
        icon: ListChecks,
        phase: 5,
        built: true,
        purpose: "Actual work: check-in, worked hours, late and overtime, work done and learning.",
      },
      {
        label: "Roster",

        to: "/people/roster",
        icon: CalendarDays,
        phase: 5,
        built: true,
        purpose: "Expected work: shift templates and the published rota.",
      },
      {
        label: "Leave",
        to: "/people/leave",
        icon: ClipboardList,
        phase: 5,
        built: true,
        purpose: "Requests, balances and approvals in one place.",
      },
      {
        label: "Tasks",
        to: "/people/tasks",
        icon: ListTodo,
        phase: 5,
        built: true,
        purpose: "Daily work: assign it, move it, and close it out.",
      },
      {
        label: "Salary",
        to: "/people/salary",
        icon: BadgeDollarSign,
        phase: 5,
        built: true,
        roles: ["owner", "manager", "employee"],
        purpose: "How each person is compensated; the employee sees their own.",
      },
      {
        label: "Payroll",
        to: "/people/payroll",
        icon: Wallet,
        phase: 5,
        built: true,
        roles: ["owner", "manager"],
        purpose: "What is payable for a pay period: compute, approve, pay, lock.",
      },
      {
        label: "Payroll",
        to: "/people/payslips",
        icon: Wallet,
        phase: 5,
        built: true,
        roles: ["employee"],
        purpose: "Your own pay history: every payslip, period by period.",
      },
      {
        label: "Reports",
        to: "/people/reports",
        icon: BarChart3,
        phase: 5,
        built: true,
        roles: WORKFORCE_REPORT_PERSONAS,
        purpose: "Headcount, attendance, leave and payroll at a glance.",
      },
    ],
  },
  {
    id: "billing",
    label: "Billing",
    blurb: "Invoices, payments and the money owed.",
    moduleCode: "billing",
    roles: ["owner", "manager"],
    items: [
      {
        label: "Dashboard",
        to: "/billing/dashboard",
        icon: LayoutDashboard,
        phase: 3,
        built: true,
        purpose: "Collections, outstanding balances, ageing and who owes what.",
      },
      {
        label: "Invoices",
        to: "/billing/invoices",
        icon: Receipt,
        phase: 3,
        built: true,
        purpose: "Send, void and print. The month-end register.",
      },
      {
        label: "Taxes",
        to: "/billing/taxes",
        icon: Percent,
        phase: 3,
        built: true,
        purpose: "The GST and other rates invoices can offer.",
      },
      {
        label: "Payments",
        to: "/billing/payments",
        icon: CreditCard,
        phase: 3,
        built: true,
        purpose: "Every payment in, direction stated explicitly.",
      },
      {
        label: "Credit notes",
        to: "/billing/credit-notes",
        icon: FileText,
        phase: 3,
        built: true,
        purpose: "Refunds, and the commission they reverse.",
      },
      {
        label: "Recurring",
        to: "/billing/recurring",
        icon: Repeat,
        phase: 3,
        built: true,
        purpose: "Memberships and retainers on a schedule.",
      },
      {
        label: "Reports",
        to: "/billing/reports",
        icon: BarChart3,
        phase: 3,
        built: true,
        purpose: "Trends, collections, ageing and CSV exports.",
      },
    ],
  },
  {
    id: "automation",
    label: "Automation",
    blurb: "Joins that turn themselves on and off without a deploy.",
    moduleCode: "automation",
    roles: ["owner"],
    items: [
      {
        label: "Rules",
        to: "/automation/rules",
        icon: Sparkles,
        phase: 6,
        built: false,
        purpose: "When X happens, and Y is true, then do Z.",
      },
    ],
  },
];

export interface SettingsItem {
  label: string;
  to: string;
  icon: LucideIcon;
  built: boolean;
  purpose?: string;
  roles?: Persona[];
}

export const SETTINGS: SettingsItem[] = [
  {
    label: "Account",
    to: "/settings",
    icon: Building2,
    built: true,
    roles: ["owner"],
    purpose: "Name, industry, timezone and currency.",
  },
  {
    label: "Plan & usage",
    to: "/settings/plan",
    icon: Cog,
    built: true,
    roles: ["owner"],
    purpose: "What you are on, and how close you are to each limit.",
  },
];

/**
 * Organisation sits directly under the product logo, ahead of the pillars.
 *
 * It is one area with two views -- the management screens (branches,
 * departments, designations, reporting lines) and the chart that renders the
 * persisted reporting relationships -- so a walk of the business starts where
 * the business is structured. Owner and manager only: employees never receive
 * the company directory.
 */
export const ORGANISATION: NavItem[] = [
  {
    label: "Organisation",
    to: "/people/organization",
    icon: Network,
    phase: 5,
    built: true,
    end: true,
    roles: ["owner", "manager"],
    purpose: "Branches, departments, designations and reporting lines.",
  },
  {
    label: "Org chart",
    to: "/people/organization/chart",
    icon: Share2,
    phase: 5,
    built: true,
    roles: ["owner", "manager"],
    purpose: "The reporting hierarchy, from each employee's manager.",
  },
];

/**
 * Self-service. Shown to every persona: these are the screens that are about
 * *you*, not about managing other people. Distinct from the People management
 * group so an employee's own work is never buried among admin screens.
 */
export const SELF_SERVICE: NavItem[] = [
  {
    label: "My desk",
    to: "/people/desk",
    icon: Clock,
    phase: 5,
    built: true,
    purpose: "Your starting screen: shift, attendance, leave and payslip.",
  },
  {
    label: "My payslips",
    to: "/people/payslips",
    icon: Wallet,
    phase: 5,
    built: true,
    roles: ["owner", "manager"],
    purpose: "Every payslip paid to you, period by period.",
  },
  {
    label: "My profile",
    to: "/people/me",
    icon: UserSquare2,
    phase: 5,
    built: true,
    purpose: "Your own employment record and timeline.",
  },
];

/** The platform operator's own books. Only rendered for superusers. */
export const PLATFORM: SettingsItem[] = [
  {
    label: "Companies",
    to: "/platform/companies",
    icon: Building2,
    built: true,
    purpose: "Every account in the book, and provisioning.",
  },
  {
    label: "Audit",
    to: "/platform/audit",
    icon: FileText,
    built: true,
    purpose: "Who touched which company and why.",
  },
];

/** Report surfaces span pillars, so they get their own group. */
export const REPORTS: Pillar = {
  id: "reports",
  label: "Reports",
  blurb: "The numbers that decide whether the business is working.",
  roles: ["owner"],
  items: [
    {
      label: "Contribution margin",
      to: "/reports/margin",
      icon: BarChart3,
      phase: 7,
      built: false,
      purpose: "Revenue from each person's book, minus their cost. The flagship.",
    },
    {
      label: "Sales & receivables",
      to: "/reports/receivables",
      icon: BarChart3,
      phase: 3,
      built: false,
      purpose: "By period, employee, service and source.",
    },
  ],
};

export function allItems(): NavItem[] {
  return [...PILLARS, REPORTS].flatMap((p) => p.items.filter((i) => i.to !== "/"));
}

export function findItem(to: string): NavItem | undefined {
  return allItems().find((i) => i.to === to);
}

/** The pillar a route belongs to, so a page can title itself correctly. */
export function pillarFor(section: PillarId): Pillar {
  return section === "reports" ? REPORTS : PILLARS.find((p) => p.id === section)!;
}

/** Whether a nav entry (pillar, item or settings row) is shown to a persona. */
export function canSee(entry: { roles?: Persona[] }, persona: Persona): boolean {
  return !entry.roles || entry.roles.includes(persona);
}

/**
 * The pillars this persona sees, with each pillar's items narrowed to the
 * persona as well. A pillar whose items all filter out is dropped, so an
 * employee never sees an empty "Billing" heading.
 */
export function pillarsFor(persona: Persona): Pillar[] {
  return PILLARS.filter((pillar) => canSee(pillar, persona))
    .map((pillar) => ({
      ...pillar,
      items: pillar.items.filter((item) => canSee(item, persona)),
    }))
    .filter((pillar) => pillar.items.length > 0);
}